import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import Button from '../components/Button/Button';
import TextInputReset from '../components/TextInputReset/TextInputReset';
import FilterCategory from '../components/FilterCategory/FilterCategory';
import Sticky from '../styled components/Sticky';
import ContainerWrapper from '../styled components/ContainerWrapper';

import { createNoFilter, getDefaultFilterName } from '../libs/eventsFilters';
import LoadingState from '../styled components/LoadingState';
import debounce from '../libs/debounce';
import FilterEditorError from '../components/Errors/FilterEditorError';
import NavigationHistory from '../libs/NavigationHistory';
import {
  HeaderActionsLeft,
  HeaderActionsRight,
  Header,
  HeaderTitle,
  HeaderContent
} from '../components/PageHeaders/PageHeader.style';

class EventFilterEditor extends React.Component {
  constructor(props) {
    const { filters, filterIndex } = props;
    super(props);
    /* Because of animations this editor can be rerendered while animating out,
       take into account that the filter it was showing has been deleted */
    const filter =
      filterIndex === -1 || filterIndex >= filters.length
        ? createNoFilter()
        : filters[filterIndex];
    this.state = {
      name: filter.name,
      isDefault: filter.isDefault,
      locations: [...filter.locations],
      statuses: [...filter.statuses],
      profiles: [...filter.profiles],
      stickyPosition: 0,
      errorCode: false
    };

    this.filterHeaderRef = React.createRef();
    this.inputFilterNameRef = React.createRef();

    /* When refreshing this page, the filter options need to be fetched again */
    if (props.eventsFilterOptions === null)
      this.getFilterOptions(props.fetchEventFilterOptions);
    this.filter = filter;
    this.renderSelectedButton = this.renderSelectedButton.bind(this);
    this.handleApplyClick = this.handleApplyClick.bind(this);
    this.handleCancelClick = this.handleCancelClick.bind(this);
    this.goToEventsWidget = this.goToEventsWidget.bind(this);
    this.handleDeleteClick = debounce(this.handleDeleteClick.bind(this));
    this.onChangeEventFilterName = this.onChangeEventFilterName.bind(this);
    this.onBlurEventFilterName = this.onBlurEventFilterName.bind(this);
    this.onKeyDownEventFilterName = this.onKeyDownEventFilterName.bind(this);
    this.handleUpdateToStateCollection = this.handleUpdateToStateCollection.bind(this);
    this.setStickyPosition = this.setStickyPosition.bind(this);
  }

  componentDidMount() {
    this.setStickyPosition();
  }

  handleUpdateToStateCollection(name, updatedCollection) {
    this.setState({
      [name]: updatedCollection
    });
  }

  handleApplyClick(event) {
    const { filters, filterIndex } = this.props;
    const { name } = this.state;
    event.preventDefault();
    const filterName = name.trim().length > 0 ? name.trim() : getDefaultFilterName();
    this.setState(
      {
        isDefault: true,
        name: filterName
      },
      () => {
        if (this.hasFilterChanged()) {
          const updatedFilters = filters.map((filter) => ({
            ...filter,
            isDefault: false
          }));
          updatedFilters[filterIndex] = {
            ...this.state
          };
          this.updateUserPreferenceEventFilters(updatedFilters, () => {
            this.goToEventsWidget();
          });
        } else {
          this.goToEventsWidget();
        }
      }
    );
  }

  handleCancelClick(event) {
    const { filters, filterIndex, editExistingFilter } = this.props;
    event.preventDefault();
    if (!editExistingFilter) {
      const updatedFilters = [...filters];
      updatedFilters.splice(filterIndex, 1);
      this.updateUserPreferenceEventFilters(updatedFilters);
    }
    this.goToEventsWidget(false);
  }

  handleDeleteClick(event) {
    const { filters, filterIndex } = this.props;
    event.preventDefault();
    const updatedFilters = [...filters];
    updatedFilters.splice(filterIndex, 1);
    this.updateUserPreferenceEventFilters(updatedFilters, this.goToEventsWidget);
  }

  onChangeEventFilterName(value) {
    this.setState({
      name: value
    });
  }

  onKeyDownEventFilterName(event) {
    const { keyCode } = event;
    if (keyCode === 13) {
      this.inputFilterNameRef.current.blur();
    }
  }

  onBlurEventFilterName() {
    const { editExistingFilter } = this.props;
    if (this.inputFilterNameRef.current.value.length === 0) {
      const defFilterName = editExistingFilter
        ? this.filter.name
        : getDefaultFilterName();
      this.setState({
        name: defFilterName
      });
    }
  }

  setStickyPosition() {
    if (this.filterHeaderRef) {
      if (this.filterHeaderRef.current) {
        this.setState({
          stickyPosition: this.filterHeaderRef.current.offsetHeight
        });
      }
    }
  }

  async getFilterOptions(fct) {
    const resultOrError = await fct();
    if (resultOrError) {
      this.setState({
        errorCode: resultOrError.code
      });
    }
  }

  goToEventsWidget(closeFilterDrawer = true) {
    const { navigationHistory, linkUrlFor } = this.props;
    const previousWidget = navigationHistory.getPreviousWidget();
    if (previousWidget && previousWidget.id === 'events')
      navigationHistory.goBack({ state: { keepFilterDrawerOpen: !closeFilterDrawer } });
    else
      navigationHistory.goToWidgetFromPopover(linkUrlFor(`/events`), {
        state: { keepFilterDrawerOpen: !closeFilterDrawer }
      });
  }

  hasFilterChanged() {
    const { name, isDefault, locations, statuses, profiles } = this.state;
    return (
      this.filter.name !== name ||
      this.filter.isDefault !== isDefault ||
      locations.length !== this.filter.locations.length ||
      statuses.length !== this.filter.statuses.length ||
      profiles.length !== this.filter.profiles.length ||
      !isEqual(statuses, this.filter.statuses) ||
      !isEqual(locations, this.filter.locations) ||
      !isEqual(profiles, this.filter.profiles)
    );
  }

  async updateUserPreferenceEventFilters(filters, callback) {
    const { setUserPreferenceEventFilters } = this.props;
    const errorOrPromise = await setUserPreferenceEventFilters(filters);
    if (errorOrPromise && errorOrPromise.code) {
      this.setState({
        errorCode: errorOrPromise.code
      });
    } else if (callback) callback();
  }

  sortFilterOptions(aCollection) {
    return aCollection.sort((a, b) => a.name.localeCompare(b.name));
  }

  renderSelectedButton() {
    return <Button contentType="icon" size="large" icon="check" color="primary" />;
  }

  renderEventFilterName() {
    const { name } = this.state;
    return (
      <ContainerWrapper $relative tw="mt-16 mt-8 ">
        <label tw="block my-8">
          <TextInputReset
            ref={this.inputFilterNameRef}
            placeholder={i18n('Enter a name')}
            onChange={this.onChangeEventFilterName}
            onKeyDown={this.onKeyDownEventFilterName}
            onBlur={this.onBlurEventFilterName}
            inputValue={name}
            data-test-input-filter-name
            enterKeyHint="done"
          />
        </label>
      </ContainerWrapper>
    );
  }

  renderLocations() {
    const { eventsFilterOptions } = this.props;
    let selectedLocations = this.state.locations;
    if (eventsFilterOptions.locations.status === 'rejected') selectedLocations = [];
    return (
      <FilterCategory
        label={i18n('Location')}
        noOptionsMessage={i18n(
          'No locations to filter on. You don’t have the “View” permission on any location.'
        )}
        filterOptions={this.sortFilterOptions(eventsFilterOptions.locations)}
        selectedOptions={selectedLocations}
        handleChangeSelectedOptions={(updatedCollection) => {
          this.handleUpdateToStateCollection('locations', updatedCollection);
        }}
        stickyPosition={this.state.stickyPosition}
      />
    );
  }

  renderStatuses() {
    const { eventsFilterOptions } = this.props;
    let selectedStatuses = this.state.statuses;
    if (eventsFilterOptions.statuses.status === 'rejected') selectedStatuses = [];
    return (
      <FilterCategory
        label={i18n('Status')}
        filterOptions={eventsFilterOptions.statuses}
        selectedOptions={selectedStatuses}
        handleChangeSelectedOptions={(updatedCollection) => {
          this.handleUpdateToStateCollection('statuses', updatedCollection);
        }}
        stickyPosition={this.state.stickyPosition}
      />
    );
  }

  renderProfiles() {
    const { eventsFilterOptions } = this.props;
    let selectedProfiles = this.state.profiles;
    if (eventsFilterOptions.statuses.status === 'rejected') selectedProfiles = [];
    return (
      <FilterCategory
        label={i18n('Profile')}
        filterOptions={this.sortFilterOptions(eventsFilterOptions.profiles)}
        selectedOptions={selectedProfiles}
        handleChangeSelectedOptions={(updatedCollection) => {
          this.handleUpdateToStateCollection('profiles', updatedCollection);
        }}
        stickyPosition={this.state.stickyPosition}
      />
    );
  }

  renderDeleteButton() {
    const { filterIndex, editExistingFilter } = this.props;
    /* The "No filter" cannot be deleted */
    if (filterIndex === -1 || !editExistingFilter) return null;
    return (
      <div tw="flex justify-center">
        <Button
          contentType="text"
          size="medium"
          variant="text"
          color="primary"
          onClick={this.handleDeleteClick}
        >
          {i18n('Delete Filter')}
        </Button>
      </div>
    );
  }

  renderHeader() {
    const { editExistingFilter } = this.props;
    return (
      <Header>
        <HeaderContent>
          <HeaderActionsLeft>
            <Button
              contentType="text"
              size="small"
              variant="text"
              onClick={this.handleCancelClick}
            >
              {i18n('Cancel')}
            </Button>
          </HeaderActionsLeft>
          <HeaderTitle>
            {editExistingFilter ? i18n('Edit Filter') : i18n('Add Filter')}
          </HeaderTitle>
          <HeaderActionsRight>
            <Button
              contentType="text"
              size="small"
              variant="text"
              color="primary"
              onClick={this.handleApplyClick}
            >
              {i18n('Apply')}
            </Button>
          </HeaderActionsRight>
        </HeaderContent>
      </Header>
    );
  }

  renderErrorMessage() {
    const { navigationHistory } = this.props;
    const { errorCode } = this.state;

    return (
      <FilterEditorError errorCode={errorCode} navigationHistory={navigationHistory} />
    );
  }

  render() {
    const { eventsFilterOptions } = this.props;
    const { errorCode } = this.state;
    if (errorCode)
      return (
        <>
          {this.renderHeader()}
          {this.renderErrorMessage()}
        </>
      );
    if (eventsFilterOptions === null) return <LoadingState />;
    return (
      <div>
        <Sticky $zIndex={100} tw="bg-silver-lighten-30 pb-16" ref={this.filterHeaderRef}>
          {this.renderHeader()}
          {this.renderEventFilterName()}
        </Sticky>
        <div tw="space-y-24 mb-40 " data-test-container="filterConditions">
          <ContainerWrapper tw="px-0">
            {this.renderLocations()}
            {this.renderStatuses()}
            {this.renderProfiles()}
          </ContainerWrapper>
        </div>
        <div tw="mb-40">{this.renderDeleteButton()}</div>
      </div>
    );
  }
}

export default EventFilterEditor;

EventFilterEditor.propTypes = {
  filterIndex: PropTypes.number.isRequired,
  /** If you're editing an existing filter the filter will
   * not be removed when canceling your changes  */
  editExistingFilter: PropTypes.bool,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      isDefault: PropTypes.bool,
      locations: PropTypes.arrayOf(PropTypes.string),
      statuses: PropTypes.arrayOf(PropTypes.string),
      profiles: PropTypes.arrayOf(PropTypes.string)
    })
  ).isRequired,
  eventsFilterOptions: PropTypes.shape({
    locations: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({
        status: PropTypes.string,
        reason: PropTypes.shape({})
      })
    ]),
    statuses: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({
        status: PropTypes.string,
        reason: PropTypes.shape({})
      })
    ]),
    profiles: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({
        status: PropTypes.string,
        reason: PropTypes.shape({})
      })
    ])
  }),
  history: PropTypes.shape({}).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  setUserPreferenceEventFilters: PropTypes.func.isRequired,
  fetchEventFilterOptions: PropTypes.func.isRequired
};

EventFilterEditor.defaultProps = {
  eventsFilterOptions: null,
  editExistingFilter: false
};
